import { AUTOMATION_SEQUENCE_STEP_TYPES } from "utils/automationSequenceModel";
import { isRecoverableFiredOpenEndedMusicRow, shouldSuppressMusicForManualDismiss } from "utils/autoMusicRoutineSegment";
import { isManuallyDismissedEvent } from "utils/automationManualDismissals";
import {
  AUTOMATION_RUNTIME_REASONS,
  createSequenceRuntimeHistoryEntry,
  trimSequenceRuntimeHistory,
} from "utils/automationSequenceRuntime";
import { trackAutomationHealthEvent } from "utils/automationHealthTelemetry";
import { getEngagementSessionId } from "utils/engagementTelemetry";
import { reportAppError } from "utils/reportAppError";

const STORAGE_KEY = "automationSequenceRuntimeState";
export const CURRENT_AUTOMATION_SCHEMA_VERSION = 2;
const SUPPORTED_AUTOMATION_SCHEMA_VERSIONS = new Set([1, CURRENT_AUTOMATION_SCHEMA_VERSION]);
export const AUTOMATION_STORAGE_WARNING_INVALID_STATE =
  "Automation recovery state was reset because saved local data was invalid.";
export const AUTOMATION_MULTI_TAB_WARNING =
  "Automations are active in another Lesson Launchpad tab. Only one tab should be used for automation execution.";
const AUTOMATION_STORAGE_ARCHIVE_PREFIX = `${STORAGE_KEY}:invalid`;
const AUTOMATION_TAB_COORDINATION_KEY = `${STORAGE_KEY}:activeTab`;
const AUTOMATION_TAB_HEARTBEAT_MS = 5000;
const AUTOMATION_TAB_STALE_MS = 15000;

export const AUTOMATION_SOFT_BLOCK_REASONS = new Set([
  AUTOMATION_RUNTIME_REASONS.ACTIVE_DATE_MISMATCH,
  AUTOMATION_RUNTIME_REASONS.AUTOMATIONS_PAUSED,
  AUTOMATION_MULTI_TAB_WARNING,
]);

export const isSoftAutomationBlockReason = (reason = "") =>
  AUTOMATION_SOFT_BLOCK_REASONS.has(String(reason || "").trim());
export const AUTOMATION_LEDGER_STATUSES = {
  PENDING: "pending",
  BLOCKED: "blocked",
  FIRED: "fired",
  RECOVERED: "recovered",
  REPLACED: "replaced",
  SKIPPED: "skipped",
  NOOP: "noop",
};
export const AUTOMATION_LEDGER_TERMINAL_STATUSES = new Set([
  AUTOMATION_LEDGER_STATUSES.BLOCKED,
  AUTOMATION_LEDGER_STATUSES.FIRED,
  AUTOMATION_LEDGER_STATUSES.RECOVERED,
  AUTOMATION_LEDGER_STATUSES.REPLACED,
  AUTOMATION_LEDGER_STATUSES.SKIPPED,
  AUTOMATION_LEDGER_STATUSES.NOOP,
]);

const toIso = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (typeof value === "string") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? "" : parsedDate.toISOString();
  }
  return "";
};

const getDateKeyFromValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString("en-CA");
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const iso = toIso(value);
  return iso ? iso.slice(0, 10) : "";
};

const getLocalDateKeyFromValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return date instanceof Date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-CA")
    : "";
};

const getEventDateKey = (event = {}) =>
  event.dateKey || getDateKeyFromValue(event.startTime) || getDateKeyFromValue(event.anchorTime);

const isDurableManualMusicDismissalRow = (row = {}) =>
  row?.stepType === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC &&
  (
    row?.details?.userDismissed === true ||
    row?.details?.playbackEndedReason === "manual"
  );

const shouldRepairCrossDateTerminalRow = (row = {}) => {
  const rowDateKey = row?.dateKey || getDateKeyFromValue(row?.scheduledStartTime || row?.anchorTime);
  const attemptedDateKey = getLocalDateKeyFromValue(row?.lastAttemptedAt || row?.updatedAt);
  if (!rowDateKey || !attemptedDateKey || rowDateKey === attemptedDateKey) return false;
  if (!isTerminalLedgerRow(row)) return false;
  return !isDurableManualMusicDismissalRow(row);
};

const repairCrossDateTerminalRow = (row = {}) => ({
  ...row,
  status: AUTOMATION_LEDGER_STATUSES.PENDING,
  reason: "",
  lastAttemptedAt: "",
  details: {},
});

const shouldRemoveCrossDateHistoryEntry = (entry = {}, repairedEventKeys = new Set()) => {
  if (!entry?.eventKey || !repairedEventKeys.has(entry.eventKey)) return false;
  const entryDateKey = entry?.dateKey || getDateKeyFromValue(entry?.scheduledStartTime || entry?.anchorTime);
  const attemptedDateKey = getLocalDateKeyFromValue(entry?.timestamp);
  if (!entryDateKey || !attemptedDateKey || entryDateKey === attemptedDateKey) return false;

  return [
    AUTOMATION_LEDGER_STATUSES.BLOCKED,
    AUTOMATION_LEDGER_STATUSES.FIRED,
    AUTOMATION_LEDGER_STATUSES.RECOVERED,
    AUTOMATION_LEDGER_STATUSES.REPLACED,
    AUTOMATION_LEDGER_STATUSES.SKIPPED,
    AUTOMATION_LEDGER_STATUSES.NOOP,
    "completed",
  ].includes(entry.status);
};

const migrateStoredState = (parsed = {}) => {
  const repairedEventKeys = new Set();
  const ledgerByDateKey = Object.entries(parsed?.ledgerByDateKey || {})
    .reduce((nextLedger, [dateKey, dateLedger]) => {
      nextLedger[dateKey] = Object.entries(dateLedger || {})
        .reduce((nextDateLedger, [eventKey, row]) => {
          const rowWithDefaults = {
            ...(row || {}),
            eventKey: row?.eventKey || eventKey,
            dateKey: row?.dateKey || dateKey,
          };

          if (shouldRepairCrossDateTerminalRow(rowWithDefaults)) {
            repairedEventKeys.add(rowWithDefaults.eventKey);
            nextDateLedger[eventKey] = repairCrossDateTerminalRow(rowWithDefaults);
            return nextDateLedger;
          }

          nextDateLedger[eventKey] = rowWithDefaults;
          return nextDateLedger;
        }, {});
      return nextLedger;
    }, {});

  return {
    schemaVersion: CURRENT_AUTOMATION_SCHEMA_VERSION,
    history: trimSequenceRuntimeHistory(
      (Array.isArray(parsed?.history) ? parsed.history : [])
        .filter((entry) => !shouldRemoveCrossDateHistoryEntry(entry, repairedEventKeys))
    ),
    ledgerByDateKey,
    storageWarning: parsed?.storageWarning || "",
    lastAutomationStateResetAt: parsed?.lastAutomationStateResetAt || "",
  };
};

const getAutomationHealthOutcome = (status, details = {}, reason = "") => {
  if (status === AUTOMATION_LEDGER_STATUSES.FIRED && details?.playbackEndedReason === "manual") {
    return "manual_closed";
  }
  if (
    status === AUTOMATION_LEDGER_STATUSES.FIRED &&
    ["duration_timeout", "playAll_complete", "ended"].includes(details?.playbackEndedReason)
  ) {
    return "auto_closed";
  }
  if (status === AUTOMATION_LEDGER_STATUSES.FIRED && details?.playbackEndedReason === "replaced") {
    return "replaced";
  }
  if (
    status === AUTOMATION_LEDGER_STATUSES.SKIPPED &&
    String(reason || "").toLowerCase().includes("catch-up window")
  ) {
    return "missed";
  }
  if (status === AUTOMATION_LEDGER_STATUSES.FIRED) return "launched";
  if (status === AUTOMATION_LEDGER_STATUSES.PENDING) return "scheduled";
  return status || "skipped";
};

const trackLedgerHealthEvent = ({ event, row, status, reason, details, timestamp, telemetryContext = {} }) => {
  const eventForTelemetry = event || {
    key: row?.eventKey,
    dateKey: row?.dateKey,
    sequenceId: row?.sequenceId,
    stepId: row?.stepId,
    classId: row?.classId,
    type: row?.stepType,
    startTime: row?.scheduledStartTime,
    endTime: row?.scheduledEndTime,
  };
  trackAutomationHealthEvent({
    event: eventForTelemetry,
    outcome: getAutomationHealthOutcome(status || row?.status, details || row?.details, reason || row?.reason),
    reason: reason || row?.reason,
    details: {
      ...(telemetryContext || {}),
      ...(details || row?.details || {}),
      stepType: eventForTelemetry?.type || row?.stepType,
    },
    timestamp: timestamp || new Date(),
  });
};

const cloneLedgerByDateKey = (ledgerByDateKey = {}) =>
  Object.entries(ledgerByDateKey || {}).reduce((nextLedger, [dateKey, dateLedger]) => {
    nextLedger[dateKey] = { ...(dateLedger || {}) };
    return nextLedger;
  }, {});

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isValidLedgerByDateKey = (ledgerByDateKey) =>
  isPlainObject(ledgerByDateKey) &&
  Object.values(ledgerByDateKey).every((dateLedger) => isPlainObject(dateLedger));

const buildLedgerEvent = (dateKey, event = {}, existing = {}, options = {}) => {
  const nowIso = toIso(options.updatedAt || new Date());
  const existingIsTerminal = AUTOMATION_LEDGER_TERMINAL_STATUSES.has(existing?.status);
  const status = existing?.status || AUTOMATION_LEDGER_STATUSES.PENDING;

  return {
    eventKey: event.key || existing.eventKey || "",
    dateKey,
    sequenceId: event.sequenceId || existing.sequenceId || "",
    sequenceName: event.sequenceName || existing.sequenceName || "Automated routine",
    stepId: event.stepId || existing.stepId || "",
    stepTitle: event.title || existing.stepTitle || event.type || "Step",
    stepType: event.type || existing.stepType || "",
    classId: event.classId || existing.classId || "",
    className: event.className || existing.className || "",
    scheduledStartTime: toIso(event.startTime) || existing.scheduledStartTime || "",
    scheduledEndTime: toIso(event.endTime) || existing.scheduledEndTime || "",
    anchorTime: toIso(event.anchorTime) || existing.anchorTime || "",
    status,
    reason: existingIsTerminal ? (existing.reason || "") : (existing.reason || ""),
    warning: event.warning || options.warning || existing.warning || "",
    lastAttemptedAt: existing.lastAttemptedAt || "",
    updatedAt: nowIso,
    details: existing.details || {},
  };
};

const getRowsByStatus = (ledger = {}, statuses = []) => {
  const statusSet = new Set(statuses);
  return Object.values(ledger || {}).filter((row) => statusSet.has(row?.status));
};

const isTerminalLedgerRow = (row = {}) => {
  if (!AUTOMATION_LEDGER_TERMINAL_STATUSES.has(row?.status)) return false;
  if (
    row.status === AUTOMATION_LEDGER_STATUSES.BLOCKED &&
    isSoftAutomationBlockReason(row.reason)
  ) {
    return false;
  }
  return true;
};

const TERMINAL_RECONCILIATION_RANK = {
  [AUTOMATION_LEDGER_STATUSES.FIRED]: 5,
  [AUTOMATION_LEDGER_STATUSES.RECOVERED]: 5,
  [AUTOMATION_LEDGER_STATUSES.REPLACED]: 4,
  [AUTOMATION_LEDGER_STATUSES.SKIPPED]: 3,
  [AUTOMATION_LEDGER_STATUSES.NOOP]: 2,
  [AUTOMATION_LEDGER_STATUSES.BLOCKED]: 1,
};

const rowsMatchScheduledOccurrence = (row = {}, event = {}) =>
  String(row.sequenceId || "") === String(event.sequenceId || "") &&
  String(row.stepId || "") === String(event.stepId || "") &&
  toIso(row.anchorTime) === toIso(event.anchorTime) &&
  toIso(row.scheduledStartTime) === toIso(event.startTime) &&
  toIso(row.scheduledEndTime) === toIso(event.endTime);

const chooseMoreCompleteTerminalRow = (left = {}, right = {}) => {
  const leftRank = TERMINAL_RECONCILIATION_RANK[left?.status] || 0;
  const rightRank = TERMINAL_RECONCILIATION_RANK[right?.status] || 0;
  if (rightRank > leftRank) return right;
  return left;
};

const findDateRuleTerminalAlias = (dateLedger = {}, event = {}, existing = {}) => {
  if (event?.triggerMode !== "dateRules") return existing;

  return Object.values(dateLedger || {})
    .filter((row) =>
      row?.eventKey &&
      row.eventKey !== event.key &&
      isTerminalLedgerRow(row) &&
      rowsMatchScheduledOccurrence(row, event)
    )
    .reduce(chooseMoreCompleteTerminalRow, existing);
};

const buildTabId = () =>
  `automation-tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const parseStoredTab = (stored) => {
  try {
    const parsed = JSON.parse(stored || "{}");
    return isPlainObject(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

export class AutomationSequenceRuntimeStateManager {
  constructor({ storageKey = STORAGE_KEY } = {}) {
    this.storageKey = storageKey;
    this.archivePrefix = storageKey === STORAGE_KEY
      ? AUTOMATION_STORAGE_ARCHIVE_PREFIX
      : `${storageKey}:invalid`;
    this.tabCoordinationKey = storageKey === STORAGE_KEY
      ? AUTOMATION_TAB_COORDINATION_KEY
      : `${storageKey}:activeTab`;
    this.tabId = buildTabId();
    this.tabHeartbeatInterval = null;
    this.tabSurface = {
      isPresentSurface: false,
      isPresentVisible: false,
    };
    this.handleStorageEvent = this.handleStorageEvent.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.state = {
      loading: false,
      error: "",
      currentSnapshot: null,
      history: [],
      ledgerByDateKey: {},
      schemaVersion: CURRENT_AUTOMATION_SCHEMA_VERSION,
      storageWarning: "",
      lastAutomationStateResetAt: "",
      multiTab: {
        tabId: this.tabId,
        isPrimary: true,
        isSecondary: false,
        warning: "",
        isPresentSurface: false,
        isPresentVisible: false,
        secondarySinceMs: null,
      },
      lastRuntimeTickAt: "",
      lastRuntimeTickGapMs: 0,
      lastRuntimeWakeDetectedAt: "",
    };
    this.listeners = [];
    this.loadFromStorage();
  }

  validateStoredState(parsed) {
    if (!isPlainObject(parsed)) return false;
    if (!SUPPORTED_AUTOMATION_SCHEMA_VERSIONS.has(parsed.schemaVersion)) return false;
    if (!Array.isArray(parsed.history)) return false;
    if (!isValidLedgerByDateKey(parsed.ledgerByDateKey || {})) return false;
    return true;
  }

  archiveInvalidStoredState(rawState) {
    try {
      if (typeof localStorage === "undefined" || !rawState) return;
      localStorage.setItem(`${this.archivePrefix}:${Date.now()}`, rawState);
    } catch (error) {
      console.warn("Failed to archive invalid automation runtime state:", error);
      reportAppError(error, {
        feature: "automation_routines",
        action: "archive_invalid_runtime_state",
        severity: "warning",
        dedupeKey: "automation_routines:archive_invalid_runtime_state",
      });
    }
  }

  loadFromStorage() {
    try {
      if (typeof localStorage === "undefined") return;
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (!this.validateStoredState(parsed)) {
        this.archiveInvalidStoredState(stored);
        this.state = {
          ...this.state,
          storageWarning: AUTOMATION_STORAGE_WARNING_INVALID_STATE,
        };
        this.saveToStorage();
        return;
      }
      const migrated = migrateStoredState(parsed);
      this.state = {
        ...this.state,
        ...migrated,
        ledgerByDateKey: cloneLedgerByDateKey(migrated.ledgerByDateKey),
      };
      if (parsed.schemaVersion !== CURRENT_AUTOMATION_SCHEMA_VERSION) {
        this.saveToStorage();
      }
    } catch (error) {
      console.warn("Failed to load automation sequence runtime state:", error);
      reportAppError(error, {
        feature: "automation_routines",
        action: "load_runtime_state",
        severity: "warning",
        dedupeKey: "automation_routines:load_runtime_state",
      });
      try {
        const stored = typeof localStorage === "undefined" ? "" : localStorage.getItem(this.storageKey);
        this.archiveInvalidStoredState(stored);
      } catch (archiveError) {
        console.warn("Failed to inspect invalid automation sequence runtime state:", archiveError);
        reportAppError(archiveError, {
          feature: "automation_routines",
          action: "inspect_invalid_runtime_state",
          severity: "warning",
          dedupeKey: "automation_routines:inspect_invalid_runtime_state",
        });
      }
      this.state = {
        ...this.state,
        storageWarning: AUTOMATION_STORAGE_WARNING_INVALID_STATE,
      };
      this.saveToStorage();
    }
  }

  saveToStorage() {
    try {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          schemaVersion: CURRENT_AUTOMATION_SCHEMA_VERSION,
          history: trimSequenceRuntimeHistory(this.state.history),
          ledgerByDateKey: this.state.ledgerByDateKey || {},
          storageWarning: this.state.storageWarning || "",
          lastAutomationStateResetAt: this.state.lastAutomationStateResetAt || "",
        })
      );
    } catch (error) {
      console.warn("Failed to save automation sequence runtime state:", error);
      reportAppError(error, {
        feature: "automation_routines",
        action: "save_runtime_state",
        severity: "warning",
        dedupeKey: "automation_routines:save_runtime_state",
      });
    }
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((candidate) => candidate !== listener);
    };
  }

  notify() {
    this.listeners.forEach((listener) => listener(this.state));
  }

  updateState(updates, shouldPersist = false) {
    this.state = { ...this.state, ...updates };
    if (shouldPersist) {
      this.saveToStorage();
    }
    this.notify();
  }

  setLoading(loading) {
    this.updateState({ loading });
  }

  setError(error) {
    this.updateState({ error: error ? String(error) : "" });
  }

  markRuntimeTick({ timestamp = new Date(), gapMs = 0, wakeDetected = false } = {}) {
    this.updateState({
      lastRuntimeTickAt: toIso(timestamp),
      lastRuntimeTickGapMs: Math.max(0, Number(gapMs) || 0),
      lastRuntimeWakeDetectedAt: wakeDetected ? toIso(timestamp) : this.state.lastRuntimeWakeDetectedAt || "",
    });
  }

  getTelemetryContext(extra = {}) {
    const multiTab = this.state.multiTab || {};
    return {
      engagementSessionId: getEngagementSessionId() || "",
      isPresentSurface: Boolean(multiTab.isPresentSurface),
      isPresentVisible: Boolean(multiTab.isPresentVisible),
      isPrimaryTab: !multiTab.isSecondary,
      runtimeTickGapMs: Math.max(0, Number(extra.runtimeTickGapMs ?? this.state.lastRuntimeTickGapMs) || 0),
    };
  }

  setSnapshot(snapshot) {
    if (!snapshot?.activeDateKey) {
      this.updateState({ currentSnapshot: snapshot });
      return;
    }

    const warning = snapshot?.resolvedSchedule?.isNoSchool
      ? "This date is marked No School on your schedule."
      : "";
    const ledgerByDateKey = this.materializePendingEvents(
      this.state.ledgerByDateKey,
      snapshot.activeDateKey,
      snapshot.events || [],
      { warning, pruneMissingEvents: true }
    );

    this.updateState({ currentSnapshot: snapshot, ledgerByDateKey }, true);
    this.pruneOldLedgers(14);
  }

  materializePendingEvents(ledgerByDateKey, dateKey, events = [], options = {}) {
    if (!dateKey) return ledgerByDateKey || {};
    const nextLedgerByDateKey = cloneLedgerByDateKey(ledgerByDateKey);
    const dateLedger = { ...(nextLedgerByDateKey[dateKey] || {}) };
    const activeEventKeys = new Set(
      (Array.isArray(events) ? events : [])
        .map((event) => event?.key)
        .filter(Boolean)
    );

    (Array.isArray(events) ? events : []).forEach((event) => {
      if (!event?.key) return;
      const existing = findDateRuleTerminalAlias(dateLedger, event, dateLedger[event.key] || {});
      dateLedger[event.key] = buildLedgerEvent(dateKey, event, existing, options);
      if (!existing.eventKey && !existing.status) {
        trackLedgerHealthEvent({
          event: { ...event, dateKey },
          status: AUTOMATION_LEDGER_STATUSES.PENDING,
          reason: "Automation step scheduled.",
          timestamp: options.updatedAt || new Date(),
          telemetryContext: this.getTelemetryContext(options),
        });
      }
    });

    // Only when syncing a full schedule snapshot — never when recording a single event.
    if (options.pruneMissingEvents) {
      Object.keys(dateLedger).forEach((eventKey) => {
        if (!activeEventKeys.has(eventKey)) {
          delete dateLedger[eventKey];
        }
      });
    }

    nextLedgerByDateKey[dateKey] = dateLedger;
    return nextLedgerByDateKey;
  }

  upsertPendingEvents(dateKey, events = [], options = {}) {
    const ledgerByDateKey = this.materializePendingEvents(
      this.state.ledgerByDateKey,
      dateKey,
      events,
      options
    );
    this.updateState({ ledgerByDateKey }, true);
    return ledgerByDateKey[dateKey] || {};
  }

  updateLedgerEvent(dateKey, eventKey, patch = {}, options = {}) {
    if (!dateKey || !eventKey) return null;
    const ledgerByDateKey = cloneLedgerByDateKey(this.state.ledgerByDateKey);
    const dateLedger = { ...(ledgerByDateKey[dateKey] || {}) };
    const existing = dateLedger[eventKey] || { eventKey, dateKey };
    const nextRow = {
      ...existing,
      ...patch,
      eventKey,
      dateKey,
      updatedAt: toIso(patch.updatedAt || new Date()),
    };
    dateLedger[eventKey] = nextRow;
    ledgerByDateKey[dateKey] = dateLedger;
    this.updateState({ ledgerByDateKey }, true);
    if (!options.skipHealthTelemetry && (patch.status || patch.reason || patch.details)) {
      trackLedgerHealthEvent({
        row: nextRow,
        status: nextRow.status,
        reason: nextRow.reason,
        details: nextRow.details,
        timestamp: patch.updatedAt || new Date(),
        telemetryContext: this.getTelemetryContext(patch.details),
      });
    }
    return nextRow;
  }

  getLedgerForDate(dateKey) {
    return this.state.ledgerByDateKey?.[dateKey] || {};
  }

  getLedgerEvent(dateKey, eventKey) {
    return this.getLedgerForDate(dateKey)?.[eventKey] || null;
  }

  getTerminalEventKeysForDate(dateKey) {
    return new Set(
      Object.values(this.getLedgerForDate(dateKey))
        .filter(isTerminalLedgerRow)
        .map((row) => row.eventKey)
    );
  }

  getPendingEventsForDate(dateKey) {
    return getRowsByStatus(this.getLedgerForDate(dateKey), [AUTOMATION_LEDGER_STATUSES.PENDING]);
  }

  getBlockedEventsForDate(dateKey) {
    return getRowsByStatus(this.getLedgerForDate(dateKey), [AUTOMATION_LEDGER_STATUSES.BLOCKED]);
  }

  getSkippedEventsForDate(dateKey) {
    return getRowsByStatus(this.getLedgerForDate(dateKey), [AUTOMATION_LEDGER_STATUSES.SKIPPED]);
  }

  getFiredEventsForDate(dateKey) {
    return getRowsByStatus(this.getLedgerForDate(dateKey), [
      AUTOMATION_LEDGER_STATUSES.FIRED,
      AUTOMATION_LEDGER_STATUSES.RECOVERED,
    ]);
  }

  getRecoveredEventsForDate(dateKey) {
    return Object.values(this.getLedgerForDate(dateKey))
      .filter((row) =>
        row?.status === AUTOMATION_LEDGER_STATUSES.RECOVERED ||
        Boolean(row?.details?.recoverySource)
      );
  }

  getRecoverableEvents(dateKey = this.state.currentSnapshot?.activeDateKey) {
    return Object.values(this.getLedgerForDate(dateKey))
      .filter((row) => {
        if (
          shouldSuppressMusicForManualDismiss({
            key: row?.eventKey,
            dateKey: row?.dateKey || dateKey,
            sequenceId: row?.sequenceId,
            stepId: row?.stepId,
            classId: row?.classId,
            type: row?.stepType,
          }, row)
        ) {
          return false;
        }
        if (row?.status === AUTOMATION_LEDGER_STATUSES.PENDING) {
          return true;
        }
        if (
          row?.status === AUTOMATION_LEDGER_STATUSES.BLOCKED &&
          !isTerminalLedgerRow(row)
        ) {
          return true;
        }
        if (
          row?.status === AUTOMATION_LEDGER_STATUSES.FIRED &&
          isRecoverableFiredOpenEndedMusicRow(row, {
            type: row?.stepType,
            config: { stopMode: row?.details?.stopMode },
          })
        ) {
          return true;
        }
        return false;
      });
  }

  getMissedEvents(dateKey = this.state.currentSnapshot?.activeDateKey) {
    return this.getSkippedEventsForDate(dateKey);
  }

  getPendingEvents(dateKey = this.state.currentSnapshot?.activeDateKey) {
    return this.getPendingEventsForDate(dateKey);
  }

  getBlockedEvents(dateKey = this.state.currentSnapshot?.activeDateKey) {
    return this.getBlockedEventsForDate(dateKey);
  }

  getTodayLedgerSummary(dateKey = this.state.currentSnapshot?.activeDateKey) {
    const ledgerRows = Object.values(this.getLedgerForDate(dateKey));
    return ledgerRows.reduce((summary, row) => {
      const status = row?.status || AUTOMATION_LEDGER_STATUSES.PENDING;
      summary.total += 1;
      summary[status] = (summary[status] || 0) + 1;
      return summary;
    }, {
      dateKey,
      total: 0,
      pending: 0,
      blocked: 0,
      fired: 0,
      recovered: 0,
      skipped: 0,
      noop: 0,
    });
  }

  getRoutineExecutionSummary(sequenceId, dateKey = this.state.currentSnapshot?.activeDateKey) {
    const rows = Object.values(this.getLedgerForDate(dateKey))
      .filter((row) => String(row?.sequenceId) === String(sequenceId || ""));
    return rows.reduce((summary, row) => {
      const status = row?.status || AUTOMATION_LEDGER_STATUSES.PENDING;
      summary.total += 1;
      summary[status] = (summary[status] || 0) + 1;
      return summary;
    }, {
      sequenceId,
      dateKey,
      total: 0,
      pending: 0,
      blocked: 0,
      fired: 0,
      recovered: 0,
      skipped: 0,
      noop: 0,
    });
  }

  clearLedgerForDate(dateKey) {
    if (!dateKey) return;
    const ledgerByDateKey = cloneLedgerByDateKey(this.state.ledgerByDateKey);
    delete ledgerByDateKey[dateKey];
    this.updateState({ ledgerByDateKey }, true);
  }

  pruneOldLedgers(maxDays = 14) {
    const retainedDateKeys = new Set([
      getDateKeyFromValue(new Date()),
      this.state.currentSnapshot?.activeDateKey,
    ].filter(Boolean));
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - Math.max(0, Number(maxDays) || 0));
    const cutoffKey = getDateKeyFromValue(cutoffDate);
    const ledgerByDateKey = Object.entries(this.state.ledgerByDateKey || {})
      .reduce((nextLedger, [dateKey, ledger]) => {
        if (retainedDateKeys.has(dateKey) || dateKey >= cutoffKey) {
          nextLedger[dateKey] = ledger;
        }
        return nextLedger;
      }, {});
    this.updateState({ ledgerByDateKey }, true);
  }

  resetAutomationState() {
    const resetAt = toIso(new Date());
    const currentSnapshot = this.state.currentSnapshot;
    const warning = currentSnapshot?.resolvedSchedule?.isNoSchool
      ? "This date is marked No School on your schedule."
      : "";
    let nextLedgerByDateKey = currentSnapshot?.activeDateKey
      ? this.materializePendingEvents({}, currentSnapshot.activeDateKey, currentSnapshot.events || [], {
        warning,
      })
      : {};

    // Re-apply durable manual dismissals so Clear State does not re-fire Stop'd steps.
    if (currentSnapshot?.activeDateKey && nextLedgerByDateKey[currentSnapshot.activeDateKey]) {
      const dateKey = currentSnapshot.activeDateKey;
      const dateLedger = { ...nextLedgerByDateKey[dateKey] };
      (currentSnapshot.events || []).forEach((event) => {
        if (!event?.key || !isManuallyDismissedEvent(event)) return;
        const existing = dateLedger[event.key] || {};
        dateLedger[event.key] = {
          ...existing,
          eventKey: event.key,
          dateKey,
          sequenceId: event.sequenceId || existing.sequenceId || "",
          stepId: event.stepId || existing.stepId || "",
          stepType: event.type || existing.stepType || "",
          classId: event.classId || existing.classId || "",
          status: AUTOMATION_LEDGER_STATUSES.FIRED,
          reason: "Music was manually closed.",
          details: {
            ...(existing.details || {}),
            playbackActive: false,
            playbackEndedReason: "manual",
            userDismissed: true,
          },
          updatedAt: resetAt,
        };
      });
      nextLedgerByDateKey = {
        ...nextLedgerByDateKey,
        [dateKey]: dateLedger,
      };
    }

    this.updateState({
      history: [],
      ledgerByDateKey: nextLedgerByDateKey,
      schemaVersion: CURRENT_AUTOMATION_SCHEMA_VERSION,
      storageWarning: "",
      lastAutomationStateResetAt: resetAt,
    }, true);
  }

  getMultiTabState() {
    return this.state.multiTab || {};
  }

  setTabSurface({ isPresentSurface = false, isPresentVisible = false } = {}) {
    const nextSurface = {
      isPresentSurface: Boolean(isPresentSurface),
      isPresentVisible: Boolean(isPresentSurface && isPresentVisible),
    };
    const surfaceChanged =
      this.tabSurface.isPresentSurface !== nextSurface.isPresentSurface ||
      this.tabSurface.isPresentVisible !== nextSurface.isPresentVisible;
    this.tabSurface = nextSurface;
    if (surfaceChanged) {
      this.updateTabLeadership();
    }
    return this.getMultiTabState();
  }

  writeTabLeadershipRecord({ force = false } = {}) {
    if (typeof localStorage === "undefined") return null;
    const now = Date.now();
    const record = {
      tabId: this.tabId,
      updatedAt: now,
      isPresentSurface: Boolean(this.tabSurface.isPresentSurface),
      isPresentVisible: Boolean(this.tabSurface.isPresentVisible),
      forced: Boolean(force),
    };
    localStorage.setItem(this.tabCoordinationKey, JSON.stringify(record));
    return record;
  }

  claimPrimaryTab({ force = false } = {}) {
    this.writeTabLeadershipRecord({ force });
    return this.updateTabLeadership({ forceClaim: force });
  }

  handleVisibilityChange() {
    if (typeof document === "undefined") return;
    if (this.tabSurface.isPresentSurface) {
      this.setTabSurface({
        isPresentSurface: true,
        isPresentVisible: !document.hidden,
      });
      return;
    }
    this.updateTabLeadership();
  }

  updateTabLeadership({ forceClaim = false } = {}) {
    if (typeof localStorage === "undefined") return this.getMultiTabState();

    const now = Date.now();
    const storedTab = parseStoredTab(localStorage.getItem(this.tabCoordinationKey));
    const storedUpdatedAt = Number(storedTab.updatedAt) || 0;
    const isCurrentOwner = storedTab.tabId === this.tabId;
    const isStale = !storedTab.tabId || now - storedUpdatedAt > AUTOMATION_TAB_STALE_MS;
    const nextIsPrimary = forceClaim || isCurrentOwner || isStale;

    if (nextIsPrimary) {
      this.writeTabLeadershipRecord({ force: forceClaim });
    }

    const previousSecondarySinceMs = Number(this.state.multiTab?.secondarySinceMs) || null;
    const nextMultiTab = {
      tabId: this.tabId,
      activeTabId: nextIsPrimary ? this.tabId : storedTab.tabId,
      isPrimary: nextIsPrimary,
      isSecondary: !nextIsPrimary,
      lastHeartbeatAt: toIso(new Date(now)),
      warning: nextIsPrimary ? "" : AUTOMATION_MULTI_TAB_WARNING,
      isPresentSurface: Boolean(this.tabSurface.isPresentSurface),
      isPresentVisible: Boolean(this.tabSurface.isPresentVisible),
      secondarySinceMs: nextIsPrimary
        ? null
        : (previousSecondarySinceMs || now),
    };
    this.updateState({ multiTab: nextMultiTab });
    return nextMultiTab;
  }

  handleStorageEvent(event) {
    if (event?.key === this.tabCoordinationKey) {
      this.updateTabLeadership();
    }
  }

  startTabHeartbeat() {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return () => {};
    }

    this.updateTabLeadership();
    window.addEventListener("storage", this.handleStorageEvent);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    if (!this.tabHeartbeatInterval) {
      this.tabHeartbeatInterval = window.setInterval(
        () => this.updateTabLeadership(),
        AUTOMATION_TAB_HEARTBEAT_MS
      );
    }

    return () => this.stopTabHeartbeat();
  }

  stopTabHeartbeat() {
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", this.handleStorageEvent);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
      }
      if (this.tabHeartbeatInterval) {
        window.clearInterval(this.tabHeartbeatInterval);
      }
    }
    this.tabHeartbeatInterval = null;

    try {
      if (typeof localStorage === "undefined") return;
      const storedTab = parseStoredTab(localStorage.getItem(this.tabCoordinationKey));
      if (storedTab.tabId === this.tabId) {
        localStorage.removeItem(this.tabCoordinationKey);
      }
    } catch (error) {
      console.warn("Failed to release automation tab heartbeat:", error);
      reportAppError(error, {
        feature: "automation_routines",
        action: "release_tab_heartbeat",
        severity: "warning",
        dedupeKey: "automation_routines:release_tab_heartbeat",
      });
    }
  }

  addHistoryEntry(entry) {
    if (!entry?.eventKey) return;
    const existingKey = `${entry.eventKey}|${entry.status}`;
    const existingIndex = this.state.history.findIndex(
      (candidate) => `${candidate.eventKey}|${candidate.status}` === existingKey
    );
    const nextHistory = [entry, ...this.state.history];
    if (existingIndex >= 0) {
      nextHistory.splice(existingIndex + 1, 1);
    }
    this.updateState({ history: trimSequenceRuntimeHistory(nextHistory) }, true);
  }

  recordEvent({ event, status, reason, details, timestamp }) {
    const eventDateKey = getEventDateKey(event);
    const eventTimestamp = timestamp || new Date();

    if (eventDateKey && event?.key) {
      this.upsertPendingEvents(eventDateKey, [event], { warning: event.warning });
      const existing = this.getLedgerEvent(eventDateKey, event.key);
      this.updateLedgerEvent(eventDateKey, event.key, {
        status,
        reason: reason || "",
        warning: event.warning || "",
        lastAttemptedAt: toIso(eventTimestamp),
        details: {
          ...(existing?.details || {}),
          ...(details || {}),
        },
      });
    }

    this.addHistoryEntry(
      createSequenceRuntimeHistoryEntry({
        event,
        status,
        reason,
        details,
        timestamp: eventTimestamp,
      })
    );
  }

  getHistory() {
    return this.state.history;
  }

  getTerminalEventKeys() {
    const ledgerTerminalKeys = Object.values(this.state.ledgerByDateKey || {})
      .flatMap((dateLedger) => Object.values(dateLedger || {}))
      .filter(isTerminalLedgerRow)
      .map((row) => row.eventKey);
    if (ledgerTerminalKeys.length) {
      return new Set(ledgerTerminalKeys);
    }

    return new Set(
      this.state.history
        .filter((entry) => ["fired", "blocked", "recovered", "skipped", "completed", "noop"].includes(entry.status))
        .map((entry) => entry.eventKey)
    );
  }

  clearHistory() {
    this.updateState({ history: [] }, true);
  }

  getState() {
    return this.state;
  }
}

const automationSequenceRuntimeStateManager = new AutomationSequenceRuntimeStateManager();

export default automationSequenceRuntimeStateManager;
